import { useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { Menu, Back, Guide } from "../assets/svgs/icons";
import { LogoDefault } from "../assets/svgs/logos";
import Primary from "./btn/Primary";
import Toggle from "./Toggle";
import Btn from "./btn";
import Progress from "./progress";
import { useUserQuery } from "../views/tutorV2/hooks/queries/useUserQuery";
import useAirHeaderView from "../views/tutorV2/hooks/presentation/useAirHeaderView";

// AirHeader Props
export interface AirHeaderProps {
  /** Header type: main (로고), sub (타이틀), main-scroll (스크롤 시 그림자) */
  type?: "main" | "sub" | "main-scroll";
  /** 진행 상황 (0-100), 표시하려면 값 전달 */
  progress?: number;
  /** sub 타입일 때 표시할 타이틀 (기본값: "my AiR") */
  title?: string;
  /** 우측에 표시할 아이콘 */
  rightIcon?: React.ReactNode;
  /** 우측 아이콘 클릭 시 콜백 */
  onRightIconClick?: () => void;
}

const AirHeader: React.FC<AirHeaderProps> = ({
  type = "main",
  progress,
  title,
  rightIcon,
  onRightIconClick,
}) => {
  const { isLoggedIn } = useUserQuery();
  const {
    subBtn,
    internalToggleActive,
    isMainPage,
    handleToggleMode,
    handleClickGuide,
    handleClickLogin,
    handleClickMenu,
    handleClickBack,
    handleClickLogo,
  } = useAirHeaderView();

  const [scrolled, setScrolled] = useState(false);

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = debounce(() => {
      setScrolled(window.scrollY > 0);
    }, 50);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, []);

  // main-scroll 타입이거나 스크롤 시 그림자 적용.
  const shouldShowShadow =
    type === "main-scroll" || (type === "main" && scrolled);
  const shadowClass = shouldShowShadow
    ? "shadow-[0px_6px_12px_-6px_rgba(0,0,0,0.12),0px_8px_24px_-4px_rgba(0,0,0,0.08)]"
    : "";

  return (
    <header
      className={`air-main-header relative flex items-center justify-center bg-white w-full h-[56px] md:h-[64px] ${shadowClass}`}
    >
      {/* ============ Mobile Main - Guest ============ */}
      {type === "main" && !isLoggedIn && (
        <>
          {/* Mobile: Guide icon (좌측) - 피그마 디자인에 맞춤 */}
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 p-4 md:hidden"
            aria-label="가이드"
            onClick={handleClickGuide}
          >
            <Guide className="w-6 h-6 text-[#212121]" />
          </button>

          {/* Mobile: 이용권 등록 버튼 (우측) - 피그마 디자인에 맞춤 */}
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 h-8 px-3 border border-gray-200 rounded text-sm text-gray-500 font-medium tracking-tight md:hidden"
            onClick={handleClickLogin}
          >
            이용권 등록
          </button>

          {/* PC: Guide icon (좌측) */}
          {/* PC 에서는 좌측 가이드 메뉴 안보이게 수정 */}
          {/* <button
            className="absolute left-0 top-1 p-4 hidden md:block"
            aria-label="가이드"
            onClick={handleClickGuide}
          >
            <Guide className="w-6 h-6 text-[#212121]" />
          </button> */}

          {/* PC: Toggle */}
          <div className="absolute left-[10px] top-1/2 -translate-y-1/2 hidden md:block">
            <Toggle
              isLeft={internalToggleActive}
              leftValue="tutor"
              leftLabel="튜터"
              rightValue="socrates"
              rightLabel="소크라테스"
              onClick={handleToggleMode}
            />
          </div>

          {/* PC: Login button */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:block">
            <Primary
              color="main-gr"
              className="w-fit"
              onClick={handleClickLogin}
            >
              로그인 하기
            </Primary>
          </div>
        </>
      )}

      {/* ============ Mobile/PC Main - User ============ */}
      {type === "main" && isLoggedIn && (
        <>
          {/* Mobile: Menu icon (좌측) */}
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 p-4 md:hidden"
            aria-label="메뉴"
            onClick={handleClickMenu}
          >
            <Menu className="w-6 h-6" fill="#212121" />
          </button>

          {/* PC: Back button (좌측) - 메인 페이지가 아닐 때만 표시 */}
          {!isMainPage && (
            <button
              className="absolute left-0 top-1 p-4 hidden md:block"
              aria-label="뒤로가기"
              onClick={handleClickBack}
            >
              <Back className="w-6 h-6 text-[#212121]" />
            </button>
          )}

          {/* 우측: Progress Circle (progress prop이 있을 때) 또는 커스텀 버튼 */}
          {progress !== undefined ? (
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              {/* 모바일: size=sm (56px 컨테이너, 32px 원형, 12px 패딩) */}
              <div className="md:hidden">
                <Progress.Circle progress={progress} size="sm" />
              </div>
              {/* 데스크탑: size=md (64px 컨테이너, 48px 원형, 8px 패딩) */}
              <div className="hidden md:block">
                <Progress.Circle progress={progress} size="md" />
              </div>
            </div>
          ) : subBtn ? (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 md:top-1/2">
              <Btn.Icon {...subBtn} />
            </div>
          ) : null}
        </>
      )}

      {/* ============ Mobile/PC Main-Scroll - User ============ */}
      {type === "main-scroll" && isLoggedIn && (
        <>
          {/* Mobile: Menu icon (좌측) */}
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 p-4 md:hidden"
            aria-label="메뉴"
            onClick={handleClickMenu}
          >
            <Menu className="w-6 h-6 text-[#212121]" />
          </button>

          {/* PC: Back button (좌측) - 메인 페이지가 아닐 때만 표시 */}
          {!isMainPage && (
            <button
              className="absolute left-0 top-1 p-4 hidden md:block"
              aria-label="뒤로가기"
              onClick={handleClickBack}
            >
              <Back className="w-6 h-6 text-[#212121]" />
            </button>
          )}

          {/* 우측 커스텀 버튼 (Mobile/PC 공통) */}
          {subBtn && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 md:top-1/2">
              <Btn.Icon {...subBtn} />
            </div>
          )}
        </>
      )}

      {/* ============ Mobile Sub - User ============ */}
      {type === "sub" && isLoggedIn && (
        <>
          {/* Mobile: Back button (좌측) */}
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 p-4"
            aria-label="뒤로가기"
            onClick={handleClickBack}
          >
            <Back className="w-6 h-6 text-[#212121]" />
          </button>

          {/* 우측 커스텀 버튼 */}
          {rightIcon ? (
            <button
              className="absolute right-0 top-1/2 -translate-y-1/2 p-4"
              aria-label="우측 버튼"
              onClick={onRightIconClick}
            >
              {rightIcon}
            </button>
          ) : subBtn ? (
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <Btn.Icon {...subBtn} size="lg" />
            </div>
          ) : null}
        </>
      )}

      {/* ============ Center: Logo or Title ============ */}
      {type === "sub" ? (
        <span className="font-bold text-[#212121] text-[14px] leading-5 tracking-[-0.28px]">
          {title || "my AiR"}
        </span>
      ) : (
        <LogoDefault
          className="w-[96px] h-[24px] md:w-[100px] md:h-[25px] cursor-pointer"
          onClick={handleClickLogo}
        />
      )}
    </header>
  );
};

export default AirHeader;
